import React, { useEffect, useRef, useState } from 'react'
import { ScanLine } from 'lucide-react'
import { apiGet, apiPost } from '../api'
import { useFacility } from '../context/FacilityContext'
import { Alert, Button, DataTable, Field, Loading, Modal, PageHeader, StatusPill } from '../components/ui'
import './pages.css'

const SALE_STATUS = {
  paid: { label: 'Betalt', tone: 'open' },
  pending: { label: 'Venter', tone: 'signal' },
  refunded: { label: 'Refundert', tone: 'muted' },
  failed: { label: 'Feilet', tone: 'danger' },
  cancelled: { label: 'Avbrutt', tone: 'danger' },
  canceled: { label: 'Avbrutt', tone: 'danger' }
}
const saleStatus = (status) =>
  SALE_STATUS[(status || '').toLowerCase()] || { label: status || '—', tone: status === 'paid' ? 'open' : 'danger' }

const COLUMNS = [
  { key: 'productName', header: 'Produkt', render: (s) => <span className="cell-title">{s.productName}</span> },
  { key: 'buyerName', header: 'Kjøper' },
  { key: 'tournamentName', header: 'Turnering', render: (s) => <span className="muted">{s.tournamentName || '—'}</span> },
  { key: 'amount', header: 'Beløp', align: 'right', className: 'cell-mono', render: (s) => `${s.amount} ${s.currency}` },
  {
    key: 'status',
    header: 'Status',
    render: (s) => {
      const st = saleStatus(s.status)
      return <StatusPill tone={st.tone}>{st.label}</StatusPill>
    }
  },
  {
    key: 'redeemed',
    header: 'Innløst',
    render: (s) => <span className={`redeem-flag${s.redeemed ? ' yes' : ''}`}>{s.redeemed ? 'Ja' : 'Nei'}</span>
  },
  {
    key: 'createdAt',
    header: 'Dato',
    className: 'cell-mono muted',
    render: (s) => (s.createdAt ? new Date(s.createdAt).toLocaleDateString('nb-NO') : '—')
  }
]

function SalesPage() {
  const { activeFacilityId } = useFacility()
  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(false)
  const [code, setCode] = useState('')
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [redeeming, setRedeeming] = useState(false)
  const [scanOpen, setScanOpen] = useState(false)

  useEffect(() => {
    if (!activeFacilityId) return
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFacilityId])

  const load = async () => {
    setLoading(true)
    try {
      const res = await apiGet(`/api/facilities/${activeFacilityId}/sales`)
      if (res.ok) setSales(await res.json())
    } finally {
      setLoading(false)
    }
  }

  const redeem = async (rawCode) => {
    const value = (rawCode || code).trim()
    if (!value) return
    setRedeeming(true)
    setError('')
    setResult(null)
    try {
      const res = await apiPost('/api/products/redeem', { code: value })
      if (res.ok) {
        setResult(await res.json())
        setCode('')
        await load()
      } else {
        const err = await res.json().catch(() => ({}))
        setError(err.message || 'Ugyldig kode eller ingen tilgang.')
      }
    } catch (err) {
      setError('Feil: ' + err.message)
    } finally {
      setRedeeming(false)
    }
  }

  const paid = sales.filter((s) => s.status === 'paid')
  const revenue = paid.reduce((sum, s) => sum + Number(s.amount || 0), 0)

  const redeemedCount = sales.filter((s) => s.redeemed).length

  return (
    <div className="page">
      <PageHeader
        title="Salg"
        subtitle="Skann QR-koden fra spilleren, eller skriv inn koden manuelt. Hver kode kan innløses én gang."
      />

      <section className="card" aria-labelledby="inn-h">
        <h2 id="inn-h" className="section-title">Innløs kode</h2>
        <form
          className="sales-form"
          onSubmit={(e) => {
            e.preventDefault()
            redeem()
          }}
        >
          <Field label="Innløsningskode">
            <input
              className="sales-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="F.eks. 7XEXBKRP"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
          <Button type="submit" disabled={redeeming}>
            {redeeming ? 'Innløser…' : 'Innløs'}
          </Button>
          <Button variant="secondary" icon={ScanLine} iconSize={20} onClick={() => setScanOpen(true)}>
            Skann QR
          </Button>
        </form>

        {(error || result) && (
          <div className="sales-results" aria-live="polite">
            {error && <Alert tone="error">{error}</Alert>}
            {result && (result.alreadyRedeemed ? (
              <Alert tone="warning">
                «{result.productName}» ({result.buyerName}) var allerede innløst{' '}
                {result.redeemedAt ? new Date(result.redeemedAt).toLocaleString('nb-NO') : ''}.
              </Alert>
            ) : (
              <Alert tone="success">
                Godkjent: «{result.productName}» for {result.buyerName} ({result.amount} {result.currency}).
              </Alert>
            ))}
          </div>
        )}
      </section>

      <section className="stack" aria-labelledby="salg-h">
        <div className="sales-head">
          <h2 id="salg-h" className="section-title">
            Alle salg{paid.length > 0 && <> · <span className="mono">{revenue.toLocaleString('nb-NO')} kr</span></>}
          </h2>
          {sales.length > 0 && (
            <span className="sales-count">{sales.length} salg · {redeemedCount} innløst</span>
          )}
        </div>
        {loading ? (
          <Loading />
        ) : sales.length === 0 ? (
          <div className="empty-inline">Ingen salg ennå.</div>
        ) : (
          <div className="tall-table">
            <DataTable columns={COLUMNS} rows={sales} minWidth={860} caption="Alle salg" />
          </div>
        )}
      </section>

      {scanOpen && (
        <QrScannerModal
          onClose={() => setScanOpen(false)}
          onScan={(text) => {
            setScanOpen(false)
            const parsed = parseCode(text)
            setCode(parsed)
            redeem(parsed)
          }}
        />
      )}
    </div>
  )
}

// Kode kan komme som ren tekst eller en URL. Trekk ut siste segment hvis URL.
function parseCode(text) {
  if (!text) return ''
  const trimmed = text.trim()
  try {
    if (trimmed.startsWith('http')) {
      const parts = trimmed.split('/').filter(Boolean)
      return (parts[parts.length - 1] || '').toUpperCase()
    }
  } catch (_) { /* noop */ }
  return trimmed.toUpperCase()
}

function QrScannerModal({ onClose, onScan }) {
  const [error, setError] = useState('')
  const scannerRef = useRef(null)
  const containerId = 'qr-reader'

  useEffect(() => {
    let html5Qr
    let cancelled = false
    const start = async () => {
      try {
        const mod = await import('html5-qrcode')
        const { Html5Qrcode } = mod
        if (cancelled) return
        html5Qr = new Html5Qrcode(containerId)
        scannerRef.current = html5Qr
        await html5Qr.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            onScan(decodedText)
          },
          () => {}
        )
      } catch (err) {
        setError('Kunne ikke starte kamera: ' + (err?.message || err))
      }
    }
    start()
    return () => {
      cancelled = true
      if (scannerRef.current) {
        scannerRef.current.stop().then(() => scannerRef.current?.clear()).catch(() => {})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal onClose={onClose} title="Skann QR-kode" size="sm">
      <div className="qr-frame">
        <div id={containerId} />
        <div className="qr-corners" aria-hidden="true"><span /><span /><span /><span /></div>
        {!error && <span className="qr-scanline" aria-hidden="true" />}
        <span className="qr-hint">Hold QR-koden innenfor rammen</span>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      <Button variant="tertiary" block onClick={onClose}>Skriv inn kode manuelt</Button>
    </Modal>
  )
}

export default SalesPage
